<script lang="ts">
  import { createContext } from 'reka-ui'
  import { type ComputedRef, type Component } from 'vue'

  export interface GatFormFieldMessage {
    variant: 'info' | 'warning' | 'error'
    /** A short message to display in the field */
    message: string
    /** A longer message to display in the field */
    messageLong?: string | Component
  }

  export interface GatFormFieldContext {
    inputLabel: ComputedRef<string | undefined>
    name: ComputedRef<string>
    required: ComputedRef<boolean | string>
    tooltipHint: ComputedRef<string | undefined>
    value: ComputedRef<any>
    isDisabled: ComputedRef<boolean>
    isReadonly: ComputedRef<boolean>
    isLoading: ComputedRef<boolean>
    isValid: ComputedRef<boolean>
    showOptional: ComputedRef<boolean>
    fieldType: ComputedRef<'default' | 'inset'>
    messages: ComputedRef<GatFormFieldMessage[]>
    errorMessages: ComputedRef<string[]>
    hasErrorMessages: ComputedRef<boolean>
    infoMessages: ComputedRef<string[]>
    warningMessages: ComputedRef<string[]>
    hasAnyMessages: ComputedRef<boolean>
    prependInnerIcon: ComputedRef<GatIconProps | undefined>
    appendInnerIcon: ComputedRef<GatIconProps | undefined>
    inputClass: ComputedRef<HTMLAttributes['class']>
    isClearable: ComputedRef<boolean>
    detailsIsVisible: ComputedRef<string | boolean | undefined>
    hint: ComputedRef<string | undefined>
    dataTestid: ComputedRef<string | undefined>
    validationErrorMessages: Ref<Set<string>>
    labelIcon: ComputedRef<GatIconProps['icon'] | undefined>
    hasAiValue: ComputedRef<boolean>
    aiHint: ComputedRef<string | undefined>
  }

  export const [useFormFieldProvider, provideFormField] =
    createContext<GatFormFieldContext>('gat-form-field-context')
</script>

<script setup lang="ts" generic="TValue">
  import { computed, ref, watch, onMounted } from 'vue'
  import type { HTMLAttributes, Ref } from 'vue'
  import { useFormField } from './useFormField'
  import type { FormFieldValidationRule } from './useFormField'
  import { useHideDetails } from '../useHideDetails'
  import { globalUtils } from '@gat-ship/utils'
  import { useGatForm } from '../gat-form/gat-form.vue'
  import { type GatIconProps } from '../gat-icon'
  import GatFormFieldMessage from './gat-form-field-message.vue'
  import GatFormFieldInset from './variants/form-field-inset.vue'
  import GatFormFieldDefault from './variants/form-field-default.vue'

  export interface GatFormFieldProps<TValue> {
    /** The HTML attribute id */
    id?: string
    /** The HTML attribute autocomplete */
    autocomplete?: string
    /** If the field is required. If a string is provided, it will be used as the error message. */
    required?: boolean | string
    /** The rules to validate the field */
    rules?: FormFieldValidationRule<TValue> | FormFieldValidationRule<TValue>[]
    /** The event to validate the field on */
    validateOn?: 'blur' | 'input' | 'manual'
    /** Resets the field to its initial state */
    reset?: () => void
    /** The value of the field */
    value?: TValue
    /** The hint to display when the field is not in an error state */
    hint?: string
    /** A tooltip hint that can be shown by clicking/hovering on the icon. */
    tooltipHint?: string
    /** The data-testid attribute */
    dataTestid?: string
    /** If the field is readonly */
    readonly?: boolean
    /** If the field is hidden */
    hideDetails?: boolean | 'auto'
    /** The class of the field */
    class?: HTMLAttributes['class']
    /** The class of the input */
    inputClass?: HTMLAttributes['class']
    /** The label of the field */
    label?: string
    /** The icon used in front of the label */
    labelIcon?: GatIconProps['icon']
    /** If the field is optional */
    showOptional?: boolean
    /** If the field is disabled */
    disabled?: boolean
    /** If the field is loading */
    loading?: boolean
    /** Appends an icon inside the input field */
    appendInnerIcon?: GatIconProps['icon'] | GatIconProps
    /** Prepends an incon inside the input field */
    prependInnerIcon?: GatIconProps['icon'] | GatIconProps
    /** Clears the field */
    clearable?: boolean
    messages?: GatFormFieldMessage[]
    /** The property name of the field from AI */
    aiExtractPropertyName?: string
    /** The type of the field */
    fieldType?: 'default' | 'inset'
  }

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<GatFormFieldProps<TValue>>(), {
    required: false,
    readonly: false,
    hideDetails: 'auto',
    value: undefined,
    validateOn: 'blur',
    as: undefined,
    tooltipSide: 'left',
    isInvalid: false,
    showOptional: true,
    autocomplete: 'off',
    fieldType: undefined
  })

  const emit = defineEmits<{
    (e: 'clear'): void
    (e: 'blur', event: FocusEvent): void
    (e: 'click', event: MouseEvent): void
    (e: 'value-updated', value: TValue | undefined): void
  }>()

  const name = computed(() => {
    return (
      props.id ??
      props.label ??
      globalUtils.functions.generateShortUniqueId('input')
    )
  })

  const fieldId = computed(() => {
    return (
      props.id ??
      `field-${name.value}-${Math.random().toString(36).substr(2, 9)}`
    )
  })

  const form = useGatForm()

  const isValid = ref(true)

  const _validationErrorMessages = ref<Set<string>>(new Set<string>())

  function setValidationErrorMessage(value: string | string[] | undefined) {
    _validationErrorMessages.value.clear()
    if (value) {
      if (Array.isArray(value)) {
        value.forEach((v) => _validationErrorMessages.value.add(v))
      } else {
        _validationErrorMessages.value.add(value)
      }
      isValid.value = false
    }
  }

  const _rules = computed(() => {
    const result: FormFieldValidationRule<TValue>[] = []
    if (props.rules) {
      if (Array.isArray(props.rules)) {
        props.rules.forEach((rule) => {
          result.push(rule)
        })
      } else {
        result.push(props.rules)
      }
    }
    if (props.required) {
      result.push((value) => {
        if (!value || (typeof value === 'string' && value.length === 0)) {
          if (typeof props.required === 'string') {
            return props.required
          }
          return 'required'
        }
        return true
      })
    }
    return result
  })

  // The `name` is returned in a function because we want to make sure it stays reactive
  // If the name changes you want `useField` to be able to pick it up
  async function validateField() {
    let result = true
    let errorMessages: string[] = []
    if (_rules.value && _rules.value.length > 0) {
      const validationResult = await (
        await Promise.all(
          _rules.value.map((rule) => rule(props.value as TValue))
        )
      ).filter((result) => result !== true)
      if (validationResult.length > 0) {
        errorMessages = validationResult.filter(
          (result) => typeof result === 'string'
        )
        result = false
      }
    }
    setValidationErrorMessage(errorMessages)
    return result
  }

  const resetField = () => {
    isValid.value = true
    _validationErrorMessages.value.clear()
    if (props.reset) {
      props.reset()
    }
  }

  const updateValue = (value: TValue | undefined) => {
    emit('value-updated', value)
  }

  // Wrapper function for component's validate method
  const validate = async (): Promise<boolean> => {
    const result = await validateField()
    isValid.value = result
    return result
  }

  // Register with form context
  const { markDirty } = useFormField({
    id: fieldId.value,
    name: name.value,
    value: props.value,
    aiExtractPropertyName: props.aiExtractPropertyName,
    validate,
    reset: resetField,
    setAiValue: (value: any, hint: string | undefined) => {
      aiValues.value.value = value
      aiValues.value.hint = hint
      updateValue(value)
    }
  })

  onMounted(() => {
    // Watch for value changes after mount to mark field as dirty
    watch(
      () => props.value,
      async (val) => {
        const isEmpty = val === undefined || val === '' || val === null
        if (props.validateOn === 'input') {
          isValid.value = await validateField()
        }
        markDirty()
      },
      { immediate: false }
    )
  })

  const { detailsIsVisible, setHideDetails } = useHideDetails(
    props,
    _validationErrorMessages,
    props.hint
  )

  function handleBlur(event: FocusEvent) {
    if (props.validateOn === 'blur') {
      validateField()
    }
    emit('blur', event)
  }

  function clear(value: TValue, toValue: any = undefined) {
    value = toValue
  }

  const _fieldType = computed(() => {
    return props.fieldType ?? form?.fieldType ?? 'default'
  })

  const messages = computed((): GatFormFieldMessage[] => {
    const messagesFromProps = props.messages ?? []
    const messagesFromContext: GatFormFieldMessage[] = Array.from(
      _validationErrorMessages.value
    ).map((message) => ({
      variant: 'error',
      message: message,
      messageLong: undefined
    }))
    return messagesFromProps.concat(messagesFromContext)
  })
  const infoMessages = computed(() =>
    messages.value
      .filter((message) => message.variant === 'info')
      .map((message) => message.message)
  )
  const warningMessages = computed(() =>
    messages.value
      .filter((message) => message.variant === 'warning')
      .map((message) => message.message)
  )
  const errorMessages = computed(() =>
    messages.value
      .filter((message) => message.variant === 'error')
      .map((message) => message.message)
  )

  const hasErrorMessages = computed(() => {
    return errorMessages.value.length > 0
  })

  const hasAnyMessages = computed(() => {
    return (
      errorMessages.value.length > 0 ||
      warningMessages.value.length > 0 ||
      infoMessages.value.length > 0
    )
  })

  const aiValues = ref<{
    value: any
    hint: string | undefined
  }>({
    value: undefined,
    hint: undefined
  })

  provideFormField({
    inputLabel: computed(() => {
      if (!props.label) {
        return undefined
      }
      let result = props.label ?? ''
      if (props.readonly || props.showOptional === false) {
        return result
      }
      return result
    }),
    fieldType: _fieldType,
    name,
    required: computed(() => props.required),
    showOptional: computed(() => props.showOptional),
    tooltipHint: computed(() => props.tooltipHint),
    value: computed(() => props.value),
    isDisabled: computed(() => props.disabled),
    isReadonly: computed(() => {
      return props.readonly || form?.readonly
    }),
    isLoading: computed(() => props.loading),
    isValid: computed(() => isValid.value),
    messages,
    infoMessages,
    warningMessages,
    errorMessages,
    hasErrorMessages,
    hasAnyMessages,
    prependInnerIcon: computed((): GatIconProps | undefined => {
      if (typeof props.prependInnerIcon === 'string') {
        return { icon: props.prependInnerIcon }
      }
      if (
        typeof props.prependInnerIcon === 'object' &&
        'icon' in props.prependInnerIcon
      ) {
        return {
          ...props.prependInnerIcon
        }
      }
      return undefined
    }),
    appendInnerIcon: computed((): GatIconProps | undefined => {
      if (typeof props.appendInnerIcon === 'string') {
        return { icon: props.appendInnerIcon }
      }
      if (
        typeof props.appendInnerIcon === 'object' &&
        'icon' in props.appendInnerIcon
      ) {
        return {
          ...props.appendInnerIcon
        }
      }
      return undefined
    }),
    inputClass: computed(() => props.inputClass),
    isClearable: computed(
      () => props.clearable && props.value !== undefined && props.value !== ''
    ),
    detailsIsVisible,
    hint: computed(() => props.hint),
    dataTestid: computed(() => props.dataTestid),
    validationErrorMessages: _validationErrorMessages,
    labelIcon: computed(() => props.labelIcon),
    hasAiValue: computed(
      () =>
        typeof aiValues.value.value !== 'undefined' &&
        aiValues.value.value === props.value
    ),
    aiHint: computed(() => aiValues.value.hint)
  })

  defineExpose({
    validate,
    reset: resetField,
    isValid,
    errorMessage: _validationErrorMessages,
    setErrorMessage: setValidationErrorMessage,
    markDirty,
    setHideDetails,
    handleBlur,
    clear
  })
</script>

<template>
  <component
    :is="_fieldType === 'inset' ? GatFormFieldInset : GatFormFieldDefault"
    v-bind="props"
    @click="emit('click', $event)"
  >
    <template #default="defaultProps" v-if="$slots['default']">
      <slot v-bind="defaultProps" />
    </template>
    <template #label-append v-if="$slots['label-append']">
      <slot name="label-append" />
    </template>
    <template #label-content v-if="$slots['label-content']">
      <slot name="label-content" />
    </template>

    <template #messages="messagesProps" v-if="$slots['messages']">
      <slot name="messages" v-bind="messagesProps" />
    </template>
    <template #message-short="messageShortProps" v-if="$slots['message-short']">
      <slot name="message-short" v-bind="messageShortProps" />
    </template>
    <template #messages-long="messagesLongProps" v-if="$slots['messages-long']">
      <slot name="messages-long" v-bind="messagesLongProps" />
    </template>
    <template
      #readonly-content="readonlyContentProps"
      v-if="$slots['readonly-content']"
    >
      <slot name="readonly-content" v-bind="readonlyContentProps" />
    </template>
    <template #prepend-inner="prependInnerProps" v-if="$slots['prepend-inner']">
      <slot name="prepend-inner" v-bind="prependInnerProps" />
    </template>
    <template #append-inner="appendInnerProps" v-if="$slots['append-inner']">
      <slot name="append-inner" v-bind="appendInnerProps" />
    </template>
    <template
      #prepend-inner-icon="prependInnerIconProps"
      v-if="$slots['prepend-inner-icon']"
    >
      <slot name="prepend-inner-icon" v-bind="prependInnerIconProps" />
    </template>
    <template
      #append-inner-icon="appendInnerIconProps"
      v-if="$slots['append-inner-icon']"
    >
      <slot name="append-inner-icon" v-bind="appendInnerIconProps" />
    </template>
    <template #clear-icon="clearIconProps" v-if="$slots['clear-icon']">
      <slot name="clear-icon" v-bind="clearIconProps" />
    </template>
    <template #loading-icon="loadingIconProps" v-if="$slots['loading-icon']">
      <slot name="loading-icon" v-bind="loadingIconProps" />
    </template>
  </component>
</template>
